import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import AuthButton from 'app/pages/Auth/components/AuthButton';
import AuthField from 'app/pages/Auth/components/AuthField';
import AuthHeading from 'app/pages/Auth/components/AuthHeading';
import AuthLoading from 'app/pages/Auth/components/AuthLoading';
import PasswordField from 'app/pages/Auth/components/PasswordField';
import { ORGANISATION_USER_ROLES, ROLE_LABELS } from 'app/pages/Users/types';
import PasswordStrengthMeter from 'app/pages/ResetPassword/components/PasswordStrengthMeter';
import { PASSWORD_REGEX, PASSWORD_REQUIREMENT } from 'app/pages/ResetPassword/password-policy';

import useInvite from '../hooks/useInvite';
import useAcceptInvite from '../hooks/useAcceptInvite';
import type { Invite } from '../types';

const schema = yup
  .object({
    forename: yup.string().trim().required('First name is required'),
    surname: yup.string().trim().required('Last name is required'),
    password: yup
      .string()
      .required('Password is required')
      .matches(PASSWORD_REGEX, { message: PASSWORD_REQUIREMENT, excludeEmptyString: true }),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], 'Passwords do not match')
      .required('Please confirm your password'),
  })
  .required();

type InvitationFormValues = yup.InferType<typeof schema>;

type ClosedState = 'expired' | 'accepted' | 'notFound' | 'unavailable';

const CLOSED_COPY: Record<ClosedState, { title: string; body: string; action: string }> = {
  expired: {
    title: 'Invitation expired',
    body: 'This invitation link has expired. Please ask your organisation admin to send you a new one.',
    action: 'Back to login',
  },
  accepted: {
    title: 'Account already set up',
    body: 'This invitation has already been used and your account is ready. You can log in now.',
    action: 'Go to login',
  },
  notFound: {
    title: 'Invitation not found',
    body: "We couldn't find this invitation. The link may be incorrect or no longer valid.",
    action: 'Back to login',
  },
  unavailable: {
    title: 'Something went wrong',
    body: "We couldn't load your invitation right now. Please try again.",
    action: 'Try again',
  },
};

const listFormat = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' });

function describeInvite({ organisationName, roles, pharmacyNames }: Invite): string {
  const roleLabels = ORGANISATION_USER_ROLES.filter((role) => roles.includes(role)).map(
    (role) => ROLE_LABELS[role]
  );
  const asRoles = listFormat.format(roleLabels);
  const where = pharmacyNames.length ? ` at ${listFormat.format(pharmacyNames)}` : '';

  return `${organisationName} has invited you to join Healthera Connect as ${asRoles}${where}.`;
}

function InvitationForm(): ReactElement {
  const { inviteId } = useParams<{ inviteId: string }>();
  const navigate = useNavigate();

  const {
    data: invite,
    error: inviteError,
    isError: inviteHasErrored,
    isFetching: isFetchingInvite,
    isInitialLoading,
    refetch: refetchInvite,
  } = useInvite(inviteId);
  const { mutate: acceptInvite, isPending: isAccepting } = useAcceptInvite(inviteId ?? '');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<InvitationFormValues>({ resolver: yupResolver(schema) });

  const passwordValue = watch('password') ?? '';

  function onSubmit({ forename, surname, password }: InvitationFormValues): void {
    if (!inviteId || isAccepting) return;

    acceptInvite({ forename, surname, password }, { onSuccess: () => navigate('/login') });
  }

  // Covers the first load and a retry after the invite failed to load.
  if (isInitialLoading || (inviteHasErrored && isFetchingInvite)) {
    return <AuthLoading message="Checking your invitation…" />;
  }

  const closedState = getClosedState(invite, inviteHasErrored, inviteError);

  if (closedState) {
    const copy = CLOSED_COPY[closedState];
    const onAction =
      closedState === 'unavailable' ? () => refetchInvite() : () => navigate('/login');

    return (
      <>
        <AuthHeading title={copy.title} description={copy.body} />
        <AuthButton type="button" onClick={onAction}>
          {copy.action}
        </AuthButton>
      </>
    );
  }

  if (!invite) return <></>;

  return (
    <>
      <AuthHeading title="Set up your account" description={describeInvite(invite)} />
      <form className="vtwo:flex vtwo:flex-col vtwo:gap-6" onSubmit={handleSubmit(onSubmit)}>
        <div className="vtwo:flex vtwo:flex-col vtwo:gap-4">
          <AuthField label="Email" value={invite.email} readOnly autoComplete="username" />
          <AuthField
            label="First name"
            autoComplete="given-name"
            error={errors.forename?.message}
            {...register('forename')}
          />
          <AuthField
            label="Last name"
            autoComplete="family-name"
            error={errors.surname?.message}
            {...register('surname')}
          />
          <PasswordField
            label="Password"
            hint={PASSWORD_REQUIREMENT}
            error={errors.password?.message}
            {...register('password')}
          >
            <PasswordStrengthMeter password={passwordValue} />
          </PasswordField>
          <PasswordField
            label="Confirm password"
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />
        </div>

        <AuthButton type="submit" isLoading={isAccepting}>
          Create account
        </AuthButton>
      </form>
    </>
  );
}

export default InvitationForm;

// Only a 404 means the invite does not exist; anything else (network, 5xx) is worth retrying.
function isNotFoundError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

function getClosedState(
  invite: Invite | undefined,
  inviteHasErrored: boolean,
  inviteError: unknown
): ClosedState | null {
  if (inviteHasErrored) return isNotFoundError(inviteError) ? 'notFound' : 'unavailable';
  if (!invite) return null;
  if (invite.status === 'accepted') return 'accepted';
  if (invite.status === 'expired') return 'expired';

  return null;
}
